import { useMemo, useState } from "react";
import { Button, Typography } from "antd";

import { PageTemplate } from "@/components/Templates/PageTemplate";
import { createCategoricalFilterValue } from "@/components/UI/TableToolbar/Filtering/filtersUtil";
import ImagingStudyImageViewer from "@/components/ImagingFileViewer/ImagingStudyImageViewer";

export type ImagingModality = "CT" | "Endoscopy";

/**
 * Demo-only "Imaging" screens: one row per imaging study (each carrying a
 * DICOM image), with a "View Images" action that opens Maya's Cornerstone
 * viewer. Gated by the `imaging-viewer` feature flag (route + menu), so it
 * only appears for the demo tenant and Clario — everyone else keeps the
 * standard CRF Imaging screen.
 *
 * Always split by modality (CT vs Endoscopy) — the two have nothing in common
 * to browse together (radiology slices vs. video captures), so a locked
 * `modality` preset filter, not a user-facing toggle, keeps each screen scoped
 * to its own records. `modality` is required: there is deliberately no
 * combined CT+Endoscopy mode.
 */
export default function ImagingViewer({
  modality,
}: {
  modality: ImagingModality;
}) {
  const [viewerStudyId, setViewerStudyId] = useState<string | null>(null);
  const presetFilters = useMemo(
    () => ({ modality: createCategoricalFilterValue([modality], "include") }),
    [modality],
  );

  const extraColumns = [
    {
      key: "view_images",
      title: "",
      width: 130,
      fixed: "right" as const,
      render: (_: unknown, row: Record<string, unknown>) => {
        const studyId = row.imaging_study_id as string | undefined;
        const seriesCount = Number(row.series_count ?? 0);
        if (!studyId || !seriesCount) {
          return (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              No images
            </Typography.Text>
          );
        }
        return (
          <Button size="small" onClick={() => setViewerStudyId(studyId)}>
            View Images
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <PageTemplate
        title={`Measures: ${modality}`}
        tooltipText={`${modality} imaging studies for this trial. Open a study to view its images.`}
        type="imaging_studies"
        constants={{
          wikiLink: "/wiki/guides/exploration-measures-imagingstudies",
          idLink: undefined,
        }}
        rowKey="imaging_study_id"
        extraColumns={extraColumns}
        presetFilters={presetFilters}
      />
      <ImagingStudyImageViewer
        imagingStudyId={viewerStudyId}
        onClose={() => setViewerStudyId(null)}
      />
    </>
  );
}
